import React from "react";
import { useNavigate } from "react-router-dom";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useVisitorData } from "../../hooks/useVisitorData";
import { useProgressStep } from "../../hooks/useProgressStep";
import { ROUTES } from "../../utils/constants";
import { useStrings } from "../../hooks/useStrings";
import { formatScheduledDate } from "../../utils/helpers";
import "./style/prefillDetails.scss";

const PrefillDetails: React.FC = () => {
  const S = useStrings();
  const navigate = useNavigate();
  const { config, themeColors } = useAppConfig();
  const { visitorFullName, visitorinfo } = useVisitorData();
  const { prefillSteps } = useProgressStep();

  const scheduledDate = visitorinfo?.scheduleCheckinDate as string | undefined;

  const orgName = config?.orgName ?? "";
  const siteName = (visitorinfo?.siteName as string) ?? "";

  const handleStart = () => {
    // If the site has a privacy policy configured, show it before the form
    // (mirrors the Contactless Check-in flow). The form is passed as the
    // continuation and prefill-details as the back target.
    const consentGiven = sessionStorage.getItem("privacyConsentGiven") === "true";
    if (config?.privacyConsentType && !consentGiven) {
      navigate(`/${ROUTES.PRIVACY_POLICY}`, {
        state: {
          nextRoute: `/${ROUTES.VISITOR_FORM}`,
          backRoute: `/${ROUTES.PRE_FILL_DETAILS}`,
        },
      });
    } else {
      navigate(`/${ROUTES.VISITOR_FORM}`);
    }
  };

  return (
    <div className="screen-prefill-details">
      {/* Welcome hero */}
      <div className="prefill-hero">
        <h1 className="prefill-welcome">
          Hello{visitorFullName ? `, ${visitorFullName}` : ""}
        </h1>
        <p className="prefill-hero-subtitle">
          {S.PREFILL_DETAILS.PAGE_SUBTITLE}
        </p>
      </div>

      {/* Visit details card */}
      <div className="prefill-card prefill-card--visit">
        <div
          className="prefill-org-avatar"
          style={{
            backgroundColor: themeColors.btnColor,
            color: themeColors.btnTextColor,
          }}
        >
          <i className="bi bi-building" />
        </div>
        <div className="prefill-org-info">
          <p className="prefill-org-label">
            {S.PREFILL_DETAILS.VISIT_LABEL}
          </p>
          <p className="prefill-org-name">
            {orgName}
            {siteName ? `, ${siteName}` : ""}
          </p>
          {scheduledDate && formatScheduledDate(scheduledDate) && (
            <p className="prefill-org-type">
              <i className="bi bi-calendar3" />
              {formatScheduledDate(scheduledDate)}
            </p>
          )}
        </div>
      </div>

      {/* Section heading */}
      <div className="prefill-section-heading">
        <h2>{S.PREFILL_DETAILS.SECTION_HEADING}</h2>
        <p>
          0 of {prefillSteps.length}{" "}
          {S.PREFILL_DETAILS.STEPS_COMPLETED_SUFFIX}
        </p>
      </div>

      {/* Individual step cards */}
      {prefillSteps.map((step) => (
        <div key={step.label} className="prefill-card prefill-step-card">
          <div
            className="prefill-step-icon"
            style={{ color: themeColors.btnColor }}
          >
            <i className={`bi ${step.icon}`} />
          </div>
          <div className="prefill-step-text">
            <span className="prefill-step-title">{step.prefillTitle}</span>
            <span className="prefill-step-desc">{step.prefillDesc}</span>
          </div>
        </div>
      ))}

      {/* CTA button */}
      <button
        type="button"
        className="btn btn-primary btn-width-full btn-shadow font-weight-semibold"
        onClick={handleStart}
      >
        {S.PREFILL_DETAILS.BTN_START}
      </button>
    </div>
  );
};

export default PrefillDetails;
